import React from 'react';
import { Form, Row, Col, Button, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';

import useUpdate from 'hooks/useUpdate';
import useFormValidation from 'hooks/useFormValidation';

import { adminUserApi } from 'api';
import {
  ADMIN_USER_TYPE,
  ADMIN_USER_ACTIVE_TYPE,
  ADMIN_EDIT_USER_VALIDATION_SCHEMA,
} from 'constants/admin/user';

const UserEditForm = ({ user, onSuccess, onCancel }) => {
  const initialValues = {
    username: user?.username || '',
    email: user?.email || '',
    phone: user?.phone || '',
    is_admin: user?.is_admin?.toString() || '',
    is_active: user?.is_active?.toString() || '',
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    isValid,
  } = useFormValidation(initialValues, ADMIN_EDIT_USER_VALIDATION_SCHEMA);

  const {
    update,
    loading,
    error: updateError,
  } = useUpdate(adminUserApi, 'patch');

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const updatedData = {
        username: values.username,
        email: values.email,
        phone: values.phone,
        is_admin: values.is_admin === 'true',
        is_active: values.is_active === 'true',
      };

      await update(user.id, updatedData);
      toast.success('User updated successfully');
      onSuccess();
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {updateError && <Alert variant="danger">{updateError}</Alert>}

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>User Name</Form.Label>
            <Form.Control
              type="text"
              name="username"
              value={values.username}
              onChange={e => handleChange('username', e.target.value)}
              onBlur={() => handleBlur('username')}
              isInvalid={touched.username && errors.username}
            />
            <Form.Control.Feedback type="invalid">
              {errors.username}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={values.email}
              onChange={e => handleChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              isInvalid={touched.email && errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group>
            <Form.Label>Phone Number</Form.Label>
            <Form.Control
              type="text"
              name="phone"
              value={values.phone}
              onChange={e => handleChange('phone', e.target.value)}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>User Type</Form.Label>
            <Form.Select
              value={values.is_admin}
              onChange={e => handleChange('is_admin', e.target.value)}
              onBlur={() => handleBlur('is_admin')}
              isInvalid={touched.is_admin && errors.is_admin}
            >
              <option value="">Select User Type...</option>
              {ADMIN_USER_TYPE.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.is_admin}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={values.is_active}
              onChange={e => handleChange('is_active', e.target.value)}
              onBlur={() => handleBlur('is_active')}
              isInvalid={touched.is_active && errors.is_active}
            >
              <option value="">Select Status...</option>
              {ADMIN_USER_ACTIVE_TYPE.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.is_active}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="success" disabled={!isValid || loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </Form>
  );
};

export default UserEditForm;
